import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { ApolloProvider } from '@apollo/client';
import { Box, Container, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { config } from './config';
import { useAuth } from './hooks/useAuth';
import { LandingPage } from './components/LandingPage';
import { AdminRoute } from './components/AdminRoute';
import { apolloClient } from './graphql/client';

// Browser detection and fixes
const isBraveOrWebkit = () => {
  const isWebkit = /webkit/i.test(navigator.userAgent);
  const isChrome = /chrome/i.test(navigator.userAgent);
  const isBrave = (navigator as any).brave !== undefined;
  
  return isBrave || (isWebkit && isChrome);
};

// Apply browser-specific fixes
if (isBraveOrWebkit()) {
  // Add browser-specific class to body for CSS targeting
  document.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('webkit-browser');
    if ((navigator as any).brave !== undefined) {
      document.body.classList.add('brave-browser');
    }
    
    // Force a repaint to fix potential rendering issues
    const root = document.getElementById('root');
    if (root) {
      root.style.transform = 'translateZ(0)';
      setTimeout(() => {
        root.style.transform = '';
      }, 100);
    }
  });
}

// Create MSAL instance with error handling
let msalInstance: PublicClientApplication;

try {
  msalInstance = new PublicClientApplication(config.msalConfig);
  
  // Add event callbacks for debugging
  msalInstance.addEventCallback((event) => {
    console.log('🔍 [MSAL Event]', event.eventType, event.payload);
    
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      console.log('✅ Login successful!', event.payload);
    } else if (event.eventType === EventType.LOGIN_FAILURE) {
      console.error('❌ Login failed!', event.payload);
    } else if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      console.log('✅ Token acquired successfully!', event.payload);
    } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
      console.error('❌ Token acquisition failed!', event.payload);
    }
  });

  console.log('✅ MSAL instance created successfully');
} catch (error) {
  console.error('❌ Failed to create MSAL instance:', error);
  throw error;
}

// Create a modern theme with TalkForTy branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1', // Indigo - modern and accessible
      light: '#8b5cf6', // Purple light
      dark: '#4f46e5', // Indigo dark
    },
    secondary: {
      main: '#f59e0b', // Amber - warm and friendly
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#10b981', // Emerald
      light: '#34d399',
      dark: '#059669',
    },
    warning: {
      main: '#f59e0b', // Amber
      light: '#fbbf24',
      dark: '#d97706',
    },
    error: {
      main: '#ef4444', // Red
      light: '#f87171',
      dark: '#dc2626',
    },
    background: {
      default: '#fafbfc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1f2937', // Gray-800
      secondary: '#6b7280', // Gray-500
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Authentication wrapper component
const AuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, login, error } = useAuth();

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#f8fafc'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Loading...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Checking authentication status
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          backgroundColor: '#f8fafc'
        }}
      >
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h6" component="div" gutterBottom>
                Authentication Error
              </Typography>
              <Typography variant="body2" paragraph>
                {error}
              </Typography>
            </Alert>
            
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Troubleshooting Steps:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ ml: 2 }}>
                <li>Check your Azure B2C configuration in the Azure portal</li>
                <li>Verify that the client ID and authority URL are correct</li>
                <li>Ensure the redirect URI (http://localhost:3000) is registered in Azure B2C</li>
                <li>Check browser console for additional error details</li>
              </Typography>
            </Box>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" paragraph>
                Current Configuration:
              </Typography>
              <Typography variant="body2" component="pre" sx={{ 
                backgroundColor: '#f0f0f0', 
                p: 2, 
                borderRadius: 1,
                fontSize: '0.75rem',
                textAlign: 'left',
                overflow: 'auto'
              }}>
                {JSON.stringify({
                  clientId: config.msalConfig.auth.clientId,
                  authority: config.msalConfig.auth.authority,
                  redirectUri: config.msalConfig.auth.redirectUri,
                  scopes: config.loginRequest.scopes
                }, null, 2)}
              </Typography>
            </Box>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <LandingPage onLogin={login} isLoading={isLoading} />
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage onLogin={login} isLoading={isLoading} />;
  }

  return <>{children}</>;
};

// Main App Component
const App: React.FC = () => {
  return (
    <MsalProvider instance={msalInstance}>
      <ApolloProvider client={apolloClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <AuthWrapper>
              <Routes>
                <Route path="/" element={<AuthenticatedRoute />} />
                <Route path="/dashboard" element={<AuthenticatedRoute />} />
              </Routes>
            </AuthWrapper>
          </Router>
        </ThemeProvider>
      </ApolloProvider>
    </MsalProvider>
  );
};

// Authenticated route component
const AuthenticatedRoute: React.FC = () => {
  return <AdminRoute />;
};

export default App;
